<header>
  <div class="menu-items">
    <!-- Parent-account switching now lives in the header avatar (web) and the footer menu (mobile),
         so the parent-account banner is gone. This banner remains only for generic admin impersonation. -->
    @if (impersonatedUser && !isParentAccountView) {
      <div class="impersonation-indicator">
        <div class="impersonation-text">
          <div class="impersonating-label" rtI18n>Impersonating:</div>
          <div class="impersonated-name">{{ getImpersonatedUserName() }}</div>
        </div>
        <button type="button" class="stop-button" (click)="stopImpersonatingUser()" rtI18n>Stop</button>
      </div>
    }
    @if (isWebView) {
      <div>
        <a routerLink="/mainmenu" style="text-decoration: none;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <img class="logo" src="/assets/images/NHA_Logo_White_Reverse.png" height="45" />
            <!-- <span style="color: white; font-weight: bold; font-size: 22px;">NHA</span> -->
          </div>
        </a>
      </div>
    }
    @if (isWebView) {
      <nav class="nav-row">
        <!-- Home sits outside the drop list so it can never be reordered or removed, and so CDK
             indices line up 1:1 with headerApps. -->
        <div id="tour-home" class="nav-slot">
          <a routerLink="/mainmenu" data-testid="nav-home" class="nav-link" [routerLinkActive]="['active-link']">
            <i class="icon-home icon-white nav-icon"></i><span class="caption" rtI18n>Home</span>
          </a>
        </div>
        <div class="nav-droplist"
             [class.editing]="isEditMode"
             cdkDropList
             [id]="isMainMenuView ? 'header-apps-drop-list' : null"
             cdkDropListOrientation="horizontal"
             [cdkDropListDisabled]="!isEditMode"
             [cdkDropListConnectedTo]="['apps-grid-drop-list']"
             [cdkDropListEnterPredicate]="canDropInHeader"
             (cdkDropListDropped)="onHeaderDrop($event)">
          @for (app of headerApps; track app.Id) {
            <div class="nav-slot" [attr.id]="tourAnchorId(app)"
                 cdkDrag [cdkDragData]="app" [cdkDragDisabled]="!isEditMode">
              <a [routerLink]="navRoute(app)"
                 [attr.data-testid]="'nav-' + app.ClickPath"
                 class="nav-link"
                 [routerLinkActive]="['active-link']">
                <span class="icon-badge-wrapper">
                  @if (app.HeaderIconClass) {
                    <i class="nav-icon icon-white" [ngClass]="app.HeaderIconClass"></i>
                  } @else if (app.IconPath) {
                    <img class="nav-icon nav-icon-img" [src]="app.IconPath" alt="" />
                  }
                  @if (badgeFor(app) > 0) {
                    <span class="menu-item-badge">
                      {{badgeFor(app) > 9 ? '9+' : badgeFor(app)}}
                    </span>
                  }
                </span>
                <!-- Titles arrive translated from the server, so no rtI18n here: the directive
                     captures innerText as its translation key and can't take an interpolation. -->
                <span class="caption">{{app.Title}}</span>
              </a>
              @if (isEditMode) {
                <button type="button" class="nav-remove-btn"
                        [attr.aria-label]="'Remove ' + app.Title + ' from the bar'"
                        [attr.data-testid]="'nav-remove-' + app.ClickPath"
                        (click)="removeFromHeader(app, $event)">
                  <i class="icon-x icon-white"></i>
                </button>
              }
            </div>
          }
          @if (pinBlockReason) {
            <div class="nav-slot nav-drop-hint nav-drop-blocked">
              <i class="icon-warning icon-yellow"></i>
              @if (pinBlockReason === 'bar-full') {
                <span rtI18n>The bar is full — remove an app first</span>
              } @else {
                <span rtI18n>This app can't be pinned to the bar</span>
              }
            </div>
          } @else if (isEditMode) {
            @for (slot of emptySlots; track slot) {
              <div class="nav-slot nav-drop-hint nav-empty-slot" rtI18n>Drag an app here</div>
            }
          }
          @if (!isEditMode && headerApps.length < MAX_HEADER_APPS) {
            <div class="nav-slot">
              <button type="button" class="nav-add-btn" data-testid="nav-add-app"
                      aria-label="Add an app to the bar"
                      (click)="onAddAppClick()">
                <span class="icon-badge-wrapper">
                  <i class="nav-icon icon-add-app icon-white"></i>
                </span>
                <span class="caption" rtI18n>Add</span>
              </button>
            </div>
          }
        </div>
      </nav>
    }
    @if (isMainMenuView && !isWebView && headerUserFirstName) {
      <div class="mobile-greeting">
        <h1 rtI18n [rtI18nParams]="{ name: headerUserFirstName }">Hello, [[name]]</h1>
      </div>
    }
    <div class="header-right-actions">
      <!-- Web-only account switcher for staff who have linked their own parent account (or are
           currently viewing it). Persistent + one click, so switching either direction no longer
           means digging through the ☰ menu. The mobile equivalent still lives in the ☰ menu. -->
      @if (isWebView && (showSwitchToParent || isParentAccountView)) {
        @if (userService.CurrentUserProfile; as profile) {
          <button type="button"
                  class="account-switcher"
                  data-testid="account-switcher-trigger"
                  aria-haspopup="menu"
                  aria-label="Switch account"
                  [matMenuTriggerFor]="accountMenu"
                  (menuOpened)="checkLinkedGuardian()">
            <span class="account-switcher-avatar">
              <user-image [userId]="profile.Id" [size]="'34px'" [profileEnabled]="false"></user-image>
            </span>
            <i class="icon-chevron-down icon-white account-switcher-caret"></i>
          </button>

          <mat-menu #accountMenu="matMenu" class="account-switch-menu">
            <div class="account-switch-heading" (click)="$event.stopPropagation()">
              <span class="account-switch-eyebrow" rtI18n>Signed in as</span>
              <span class="account-switch-name">
                @if (isParentAccountView) {
                  <span rtI18n>Parent</span>
                } @else {
                  <span rtI18n>Staff</span>
                }
              </span>
            </div>
            @if (showSwitchToParent) {
              <button type="button" mat-menu-item data-testid="account-switcher-to-parent" (click)="switchToParentAccount()">
                <i class="icon-arrows-alt-h icon-action-blue account-switch-item-icon"></i>
                <span rtI18n>Switch to Parent account</span>
              </button>
            }
            @if (isParentAccountView) {
              <button type="button" mat-menu-item data-testid="account-switcher-to-staff" (click)="stopImpersonatingUser()">
                <i class="icon-arrows-alt-h icon-action-blue account-switch-item-icon"></i>
                <span rtI18n>Switch to Staff account</span>
              </button>
            }
          </mat-menu>
        }
      }
<button type="button" aria-label="Open user menu" data-testid="user-menu-trigger-btn" [matMenuTriggerFor]="menu" (menuOpened)="checkLinkedGuardian()" class="btn btn-header btn-icon">
        <i class="icon-menu icon-white" style="width: 24px; height: 24px;"></i>
      </button>
      <mat-menu #menu="matMenu" class="header-action-menu">
        <app-language-switcher [hideNonTranslatableLanguages]="true"></app-language-switcher>
        @if (isMainMenuView) {
          <button type="button" mat-menu-item data-testid="menu-clear-notifications-item" (click)="clearNotifications.emit()" [disabled]="newNotificationCount === 0" rtI18n>Clear Notifications</button>
          <button type="button" mat-menu-item data-testid="menu-edit-apps-item" (click)="toggleEditMode.emit()">
            @if (isEditMode) {
              <span rtI18n>Stop Editing Apps</span>
            } @else {
              <span rtI18n>Edit Apps</span>
            }
          </button>
        }
        @if (isMainMenuView) {
          <button type="button" mat-menu-item data-testid="menu-unhide-apps-item" [matMenuTriggerFor]="subMenu" [disabled]="hiddenApps.length <= 0" rtI18n>Unhide Apps</button>
        }
        @if (canImpersonate) {
          <button type="button" mat-menu-item data-testid="menu-impersonate-item" (click)="impersonate()" rtI18n>Impersonate</button>
        }
        @if (showSwitchToParent) {
          <button type="button" mat-menu-item data-testid="menu-switch-parent-item" (click)="switchToParentAccount()" rtI18n>Switch to Parent account</button>
        }
        @if (showSwitchToStaff) {
          <button type="button" mat-menu-item data-testid="menu-switch-staff-item" (click)="stopImpersonatingUser()" rtI18n>Switch to Staff account</button>
        }
        @if (showAIToggle){
        <div mat-menu-item class="menu-checkbox-item">
          <mat-checkbox
            data-testid="menu-help-widget-item"
            labelPosition="before"
            [checked]="userService.CurrentUserProfile?.ShowAI"
            (change)="onMatCheckboxToggle($event)">
            <span rtI18n>Show Help Widget</span>
          </mat-checkbox>
        </div>
        }
        <div mat-menu-item class="menu-checkbox-item">
          <mat-checkbox
            data-testid="menu-guided-tours-item"
            labelPosition="before"
            [checked]="showGuidedTour"
            (change)="onGuidedTourToggle($event)">
            <span rtI18n>Show Guided Tours</span>
          </mat-checkbox>
        </div>
        <button type="button" mat-menu-item data-testid="menu-my-profile-item" (click)="profile()" rtI18n>My Profile</button>
        <button type="button" mat-menu-item data-testid="menu-logout-item" (click)="logout()" rtI18n>Logout</button>
      </mat-menu>

      <mat-menu #subMenu="matMenu">
        @for (app of hiddenApps; track app) {
          <button
            type="button"
            mat-menu-item
            (click)="unhideApp.emit(app)">
            <div class="unhide-line">
              <img src="{{app.IconPath}}" class="unhide-img" />
              <div class="unhide-text">{{app.Title}}</div>
            </div>
          </button>
        }
      </mat-menu>
    </div>
  </div>
  @if (isWebView) {
    <div class="stupidcorners"></div>
  }
</header>
